import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Instagram, Facebook, Menu, X, Info, Palette, Ruler, Handshake } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  JRA HOME Builders Corp. — concept site. Residential design-build brand with a
  bold red/charcoal identity. Portfolio shown as honestly-labeled 3D design
  renderings (their own branded marketing material), not claimed as finished
  photographs, since no completed-build photography was available.
*/

const RED = "#C8102E";
const CHARCOAL = "#2B2B2B";
const CREAM = "#FAF8F6";
const INK = "#1A1A1A";

const IG = "https://www.instagram.com/jrahomebuilders/";
const FB = "https://www.facebook.com/jrahomebuilders";
const NAME = "JRA HOME Builders";
const SLUG = "jra-home-builders-concept";
const P = "/construction/jra";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function JraConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Designs", "#designs"], ["Design Approach", "#why"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Work Sans', sans-serif" }} data-testid="jra-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Space Grotesk', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#171717] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME} Corp.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="jra-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.png`} alt={NAME} className="h-10 object-contain" />
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="jra-header-cta" className="hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-bold text-white" style={{ background: RED }}>
            Build With JRA
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="jra-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="jra-mobile-cta" className="block mt-2 text-center rounded-full px-5 py-3 text-sm font-bold text-white" style={{ background: RED }}>Build With JRA</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end" data-testid="jra-hero">
        <img src={`${P}/render-1.png`} alt="A JRA HOME Builders residential design" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${CHARCOAL}11 0%, ${CHARCOAL}CC 72%, ${CHARCOAL}F5 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Bacolod City · Serving Negros &amp; Beyond</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white font-extrabold">Be Unique. Be Distinct.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              A residential design-and-build company combining fresh, innovative design with construction experience — for clients in Negros and beyond.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="jra-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-bold text-white" style={{ background: RED }}>Build With JRA</a>
              <a href="#designs" data-testid="jra-hero-designs" className="rounded-full border border-white/50 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">See Our Designs</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="jra-location-signal">Design studios listed in Bacolod and Iloilo City</p>
          </motion.div>
        </div>
      </section>

      {/* DESIGNS */}
      <section id="designs" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Design Concepts</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">A Look at Our Home Designs</h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: `${INK}90` }}>3D design renderings from our own studio — the starting point before your home takes shape on the lot.</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-8">
          {[`${P}/render-1.png`, `${P}/render-2.png`].map((img, i) => (
            <motion.div key={img} {...fadeUp} data-testid="jra-design-item" className="rounded-2xl overflow-hidden bg-white" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
              <img src={img} alt={`JRA home design concept ${i + 1}`} className="w-full h-72 object-cover" loading="lazy" />
              <p className="px-5 py-4 text-sm" style={{ color: `${INK}80` }}>3D design rendering</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY JRA */}
      <section id="why" className="py-20 md:py-28" style={{ background: CHARCOAL }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Design Approach</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-extrabold leading-snug">Fresh Design, Real Construction Experience</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-10">
            {[
              [Palette, "Fresh, Innovative Design", "Every home starts as a design tailored to how you actually want to live."],
              [Ruler, "Design-and-Build, One Team", "Design and construction stay under one roof, so nothing gets lost in translation."],
              [Handshake, "Bacolod & Iloilo Presence", "Contact points in both cities for clients planning residential design and construction."],
            ].map(([Icon, t, b]) => (
              <motion.div key={t} {...fadeUp}>
                <Icon size={26} style={{ color: RED }} />
                <h3 className="mt-4 text-lg font-bold text-white">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-snug">Build With JRA</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Bacolod Office</div><div>Rm. 1-15, JDI Bldg., Galo St., Bacolod City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Iloilo Office</div><div>Rm 201, Golden Palm Bldg., Q. Abeto St., Mandurriao, Iloilo City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639192594821" className="hover:underline">0919 259 4821</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><a href="mailto:jraconstruct@gmail.com" className="hover:underline">jraconstruct@gmail.com</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="jra-facebook-link" className="hover:underline">facebook.com/jrahomebuilders</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Instagram size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Instagram</div><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="jra-instagram-link" className="hover:underline">instagram.com/jrahomebuilders</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="jra"
              accent={RED}
              accentText="#FFFFFF"
              heading="Build With JRA"
              subheading="Tell us about the home you're picturing — our team will follow up to schedule a design consultation."
              projectTypes={["New House Build", "Renovation", "Other"]}
              phone="+639192594821"
              phoneDisplay="0919 259 4821"
              email="jraconstruct@gmail.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#171717", color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <img src={`${P}/logo.png`} alt={NAME} className="h-9 object-contain bg-white rounded p-1" />
            <p className="mt-4 text-sm leading-relaxed">Residential design-and-build based in Bacolod City, serving Negros and beyond.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Rm. 1-15, JDI Bldg., Galo St.,<br />Bacolod City</p>
            <p className="text-sm mt-2">0919 259 4821</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
              <a href={IG} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME} Corp. Designs, pricing and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={IG} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: RED }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
